/**
 * Redacts credential / PII fields from a request body before it enters the
 * devtools / E2E call log (mirrored onto `window[globalCallsKey]` by the
 * transport). Pure, zero-dependency, portable.
 *
 * Why a dedicated module: the transport is responsible for fetch / retry /
 * timeout. Redaction is an orthogonal concern (PII hygiene at the
 * observability sink) and is kept separate so the transport stays under its
 * size budget and the redactor can be unit-tested in isolation.
 */

/**
 * Request-body field names that carry credentials or PII and must never be
 * mirrored onto the exposed devtools / E2E call log. Anything in this set is
 * replaced with the literal "[redacted]" placeholder at the record() sink.
 */
const REDACTED_FIELDS = new Set([
	"password",
	"turnstileToken",
	"email",
	"confirmEmail",
	"secret",
	"currentPassword",
	"newPassword",
	"token",
]);
const REDACTED_PLACEHOLDER = "[redacted]";

/**
 * Returns a shallow clone of `body` with sensitive top-level fields replaced
 * by "[redacted]"; primitives, null/undefined and arrays pass through
 * untouched. The original object is never mutated. When no field needed
 * redaction, the original reference is returned (no clone overhead).
 */
export function redactForLog(body: unknown): unknown {
	if (body === null || body === undefined) return body;
	if (typeof body !== "object") return body;
	if (Array.isArray(body)) return body;
	const source = body as Record<string, unknown>;
	let changed = false;
	const clone: Record<string, unknown> = {};
	for (const key of Object.keys(source)) {
		if (REDACTED_FIELDS.has(key)) {
			clone[key] = REDACTED_PLACEHOLDER;
			changed = true;
		} else {
			clone[key] = source[key];
		}
	}
	return changed ? clone : source;
}
